import React, { useState, useEffect } from 'react';
import { X, CheckCircle2, Circle, ClipboardList, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { InternalMovementView, MovementTaskView } from '../../../../models/employee';
import { ProcessTaskStatus } from '../../../../models/employee';
import { updateMovementTaskStatus, getInternalMovement } from '../../../../api/Employee/internalMovements';

interface MovementDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  movement: InternalMovementView | null;
}

const getStatusColor = (status: ProcessTaskStatus): string => {
  switch (status) {
    case ProcessTaskStatus.Completed: return 'bg-green-100 text-green-800';
    case ProcessTaskStatus.Cancelled: return 'bg-red-100 text-red-800';
    case ProcessTaskStatus.InProgress: return 'bg-blue-100 text-blue-800';
    default: return 'bg-yellow-100 text-yellow-800';
  }
};

const getStatusLabel = (status: ProcessTaskStatus): string => {
  switch (status) {
    case ProcessTaskStatus.Completed: return 'Completed';
    case ProcessTaskStatus.Cancelled: return 'Cancelled';
    case ProcessTaskStatus.InProgress: return 'In Progress';
    default: return 'Pending';
  }
};

const MovementDetailsModal: React.FC<MovementDetailsModalProps> = ({ isOpen, onClose, movement }) => {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState<MovementTaskView[]>([]);

  useEffect(() => {
    if (movement && isOpen) {
      // Load fresh data to get latest tasks
      getInternalMovement(movement.id).then(result => {
        if (!result.errors || result.errors.length === 0) {
          setTasks(result.payload?.tasks || []);
        } else {
          setTasks(movement.tasks || []);
        }
      }).catch(() => {
        setTasks(movement.tasks || []);
      });
    }
  }, [movement, isOpen]);

  const handleToggleTask = async (task: MovementTaskView) => {
    const newStatus = task.status === ProcessTaskStatus.Completed
      ? ProcessTaskStatus.Pending
      : ProcessTaskStatus.Completed;
    try {
      const result = await updateMovementTaskStatus(task.id, { status: newStatus });
      if (!result.errors || result.errors.length === 0) {
        setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
      }
    } catch {
      // silently fail
    }
  };

  if (!isOpen || !movement) return null;

  const completedCount = tasks.filter(t => t.status === ProcessTaskStatus.Completed).length;
  const percentage = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;
  const currency = movement.currencyCode || '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose}>
      <div
        className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl overflow-y-auto animate-slide-in-right"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('Movement Details')}</h3>
            <p className="text-sm text-gray-600 mt-1">{movement.employeeName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Movement Info */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <span className="text-xs text-gray-500">{t('Movement Type')}</span>
              <div className="text-sm font-medium text-gray-900">{movement.movementTypeName || t('N/A')}</div>
            </div>
            <div>
              <span className="text-xs text-gray-500">{t('Effective Date')}</span>
              <div className="text-sm font-medium text-gray-900">
                {movement.effectiveDate ? new Date(movement.effectiveDate).toLocaleDateString() : t('N/A')}
              </div>
            </div>
            <div>
              <span className="text-xs text-gray-500">{t('Template')}</span>
              <div className="text-sm font-medium text-gray-900">{movement.templateName || t('N/A')}</div>
            </div>
          </div>

          {/* Position Change */}
          <div className="bg-muted border border-border rounded-lg p-4">
            <h5 className="text-sm font-medium text-gray-700 mb-3">{t('Changes')}</h5>
            <div className="space-y-3">
              <div className="flex items-center">
                <div className="flex-1">
                  <span className="text-xs text-gray-500">{t('Position')}</span>
                  <div className="text-sm text-gray-900">{movement.currentPosition || t('N/A')}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400 mx-3 flex-shrink-0" />
                <div className="flex-1">
                  <span className="text-xs text-gray-500">{t('New Position')}</span>
                  <div className="text-sm font-medium text-gray-900">{movement.newPosition || t('N/A')}</div>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-1">
                  <span className="text-xs text-gray-500">{t('Department')}</span>
                  <div className="text-sm text-gray-900">{movement.currentDepartmentName || t('N/A')}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400 mx-3 flex-shrink-0" />
                <div className="flex-1">
                  <span className="text-xs text-gray-500">{t('New Department')}</span>
                  <div className="text-sm font-medium text-gray-900">{movement.newDepartmentName || t('N/A')}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Reason */}
          {movement.reason && (
            <div>
              <span className="text-xs text-gray-500">{t('Reason')}</span>
              <div className="text-sm text-gray-900 mt-1">{movement.reason}</div>
            </div>
          )}

          {/* Progress */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">{t('Progress')}</span>
              <span className="text-sm text-gray-600">{completedCount} / {tasks.length} ({percentage}%)</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-primary h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>

          {/* Task Checklist */}
          <div className="border-t border-border pt-6">
            <h4 className="text-md font-semibold text-gray-900 mb-4">{t('Movement Checklist')}</h4>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {tasks.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <ClipboardList className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{t('No checklist items.')}</p>
                </div>
              ) : (
                tasks.map((task) => (
                  <div key={task.id} className="flex items-start space-x-3 p-3 bg-card border border-border rounded-lg hover:border-gray-300 transition-colors">
                    <button
                      type="button"
                      onClick={() => handleToggleTask(task)}
                      className="mt-0.5 flex-shrink-0"
                      title={task.status === ProcessTaskStatus.Completed ? t('Mark as pending') : t('Mark as completed')}
                    >
                      {task.status === ProcessTaskStatus.Completed ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <Circle className="w-5 h-5 text-gray-400 hover:text-green-500 transition-colors" />
                      )}
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium ${task.status === ProcessTaskStatus.Completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                        {task.name}
                      </div>
                      <div className="flex items-center flex-wrap gap-2 mt-1">
                        {task.typeName && (
                          <span className="text-xs text-gray-600">{task.typeName}</span>
                        )}
                        {task.dueDate && (
                          <span className="text-xs text-gray-500">{t('Due')}: {new Date(task.dueDate).toLocaleDateString()}</span>
                        )}
                        {task.mandatory && (
                          <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">{t('Required')}</span>
                        )}
                      </div>
                    </div>

                    <span className={`px-2 py-1 text-xs font-medium rounded flex-shrink-0 ${getStatusColor(task.status)}`}>
                      {t(getStatusLabel(task.status))}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              {t('Close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MovementDetailsModal;
